import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  CalendarRange, Upload, Search, Send, Bot, User, Loader2, Sparkles, ListTree,
  ChevronRight, ArrowLeft, Plus, Trash2, FileText, AlertCircle, SlidersHorizontal,
  CheckCircle2, Lock, ShieldCheck, FileDown, Landmark, Pencil, FileSpreadsheet,
  FileUp, AlertTriangle, Check,
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { formatCurrency, formatPercent } from '../lib/format';
import { exportCEPdf } from '../lib/exportPdf';
import { useAuth } from '../context/AuthContext';
import { MarkdownMessage } from '../components/MarkdownMessage';
import FierSpa from './FierSpa';

const SPECIAL_LABEL = {
  rimanenze_iniziali: 'Rimanenze iniziali',
  rimanenze_finali: 'Rimanenze finali',
  imposte: "Imposte d'esercizio",
};
const TIPO_OPTIONS = [
  { v: 'sal', l: 'SAL (avanzamento lavori)' },
  { v: 'fattura_attesa', l: 'Costi in attesa di fattura' },
  { v: 'magazzino', l: 'Magazzino / Rimanenze' },
  { v: 'imposte', l: 'Imposte' },
  { v: 'altro', l: 'Altro' },
];
const TIPO_LABEL = TIPO_OPTIONS.reduce((a, o) => { a[o.v] = o.l; return a; }, {});

// --- Scostamenti (rettifiche gestionali guidate) ---
const NATURE = [{ v: 'ricavo', l: 'Ricavo' }, { v: 'costo', l: 'Costo' }];
const VOCI_PRESET = {
  ricavo: ['Fatture da emettere', 'SAL cantiere', 'Assunzione di margine su prodotti non ancora usciti', 'Altro'],
  costo: ['Fatture da ricevere', 'Costi da sostenere', 'Costi sostenuti non di competenza', 'Altro'],
};
const fmtDateShort = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const SUGGESTIONS = [
  "Riassumi l'andamento del periodo",
  'Quali sono le prime voci di costo?',
  'Come si compone il costo del venduto?',
  'Ci sono conti non ancora classificati?',
];

/* ---------- POPUP VERIFICA / ALLINEAMENTO CONTI ---------- */
const ReconciliationDialog = ({ open, onOpenChange, nuovi, sezioni, voceEsclusione, onConfirm }) => {
  const [assign, setAssign] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setAssign({}); }, [open, nuovi]);
  const list = nuovi || [];
  const done = list.filter((a) => assign[a.conto]).length;
  const allDone = list.length > 0 && done === list.length;
  const confirm = async () => {
    if (!allDone) return;
    setBusy(true);
    try { await onConfirm(assign); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertCircle className="h-4 w-4 text-[hsl(var(--warning))]" />
            Verifica conti · allinea il Piano dei Conti
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Sono presenti <b className="text-foreground">{list.length}</b> conti nuovi / da allineare rispetto al Piano dei Conti.
          Assegna una sezione a ciascuno per completare il caricamento: la scelta aggiorna il <b className="text-foreground">Piano dei Conti</b> (unica fonte) e sblocca la generazione del bilancio di periodo.
        </p>
        <div className="max-h-[46vh] overflow-y-auto overflow-x-auto border rounded-lg">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow><TableHead className="w-[90px] md:w-[110px]">Conto</TableHead><TableHead>Descrizione</TableHead><TableHead className="w-[150px] md:w-[220px]">Assegna a sezione</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {list.map((a, i) => (
                <TableRow key={a.conto} data-testid={`reconcile-row-${i}`} className={assign[a.conto] ? 'bg-[hsl(var(--success))]/5' : ''}>
                  <TableCell className="tabular-nums text-xs text-muted-foreground align-top">{a.conto}</TableCell>
                  <TableCell className="text-sm max-w-[120px] whitespace-normal break-words md:max-w-none">{a.descrizione}</TableCell>
                  <TableCell>
                    <Select value={assign[a.conto] || undefined} onValueChange={(v) => setAssign((s) => ({ ...s, [a.conto]: v }))}>
                      <SelectTrigger className="h-8" data-testid={`reconcile-assign-${a.conto}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Sezioni CE</SelectLabel>
                          {(sezioni || []).map((sz) => <SelectItem key={sz} value={sz}>{sz}</SelectItem>)}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Altro</SelectLabel>
                          <SelectItem value={voceEsclusione || 'Escluso'}>Escluso (patrimoniale)</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between pt-1">
          <span className={`text-sm tabular-nums ${allDone ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}>{done}/{list.length} assegnati</span>
          <Button onClick={confirm} disabled={!allDone || busy} data-testid="reconcile-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Conferma e sblocca bilancio
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- LISTA PERIODI ---------- */
const PeriodiList = ({ annuali, periodi, onOpen, onOpenAnnual, onOpenWizard, isAdmin, onDelete }) => (
  <div className="space-y-6" data-testid="periodi-list">
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Landmark className="h-5 w-5 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Bilanci Fier S.p.A.</h1>
          <p className="text-sm text-muted-foreground">Bilanci riclassificati ufficiali (annuali) e bilanci gestionali di periodo · Fier S.p.A.</p>
        </div>
      </div>
      {isAdmin && (
        <Button onClick={onOpenWizard} data-testid="carica-bilancio-btn" className="w-full md:w-auto">
          <Upload className="h-4 w-4 mr-2" />Carica bilancio
        </Button>
      )}
    </div>

    {/* Bilanci annuali ufficiali */}
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[hsl(var(--success))]" />
        <h2 className="text-sm font-semibold text-foreground">Bilanci annuali (ufficiali)</h2>
      </div>
      {(!annuali || annuali.length === 0) ? (
        <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="annuali-empty">Nessun bilancio annuale caricato.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {annuali.map((b) => (
            <Card key={b.year} onClick={() => onOpenAnnual(b.year)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onOpenAnnual(b.year); }}
              className="p-5 cursor-pointer hover:shadow-md hover:border-primary/40 transition-shadow" data-testid={`annuale-card-${b.year}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Landmark className="h-4 w-4 text-primary" />
                  <span className="font-semibold">Bilancio annuale {b.year}</span>
                </div>
                <Badge variant="outline" className="text-[10px] text-[hsl(var(--success))] border-[hsl(var(--success))]/40 gap-1"><ShieldCheck className="h-3 w-3" />Ufficiale</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><div className="text-[11px] text-muted-foreground">Ricavi</div><div className="text-sm font-semibold tabular-nums">{formatCurrency(b.ricavi || 0)}</div></div>
                <div><div className="text-[11px] text-muted-foreground">EBITDA</div><div className={`text-sm font-semibold tabular-nums ${(b.ebitda || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(b.ebitda || 0)}</div></div>
                <div><div className="text-[11px] text-muted-foreground">Utile</div><div className={`text-sm font-semibold tabular-nums ${(b.utile_bilancio || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(b.utile_bilancio || 0)}</div></div>
              </div>
              <div className="mt-3 pt-3 border-t flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{b.n_accounts} conti</span>
                <span className="inline-flex items-center gap-1 text-primary font-medium">Apri <ChevronRight className="h-3 w-3" /></span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>

    {/* Bilanci di periodo */}
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <CalendarRange className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground">Bilanci di periodo (gestionali)</h2>
      </div>
      {periodi.length === 0 ? (
        <Card className="p-10 text-center">
          <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-foreground font-medium mb-1">Nessun periodo caricato</p>
          <p className="text-sm text-muted-foreground mb-4">Importa un mastrino di periodo in PDF (es. al 30.06) per generare il bilancio gestionale.</p>
          {isAdmin && <Button onClick={onOpenWizard} data-testid="carica-bilancio-empty-btn"><Upload className="h-4 w-4 mr-2" />Carica bilancio</Button>}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {periodi.map((p) => (
            <Card key={p.id} onClick={() => onOpen(p.id)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onOpen(p.id); }}
              className="p-5 cursor-pointer hover:shadow-md hover:border-primary/40 transition-shadow" data-testid={`periodo-card-${p.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CalendarRange className="h-4 w-4 text-primary" />
                  <span className="font-semibold">{p.label}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {p.n_nuovi > 0
                    ? <Badge variant="outline" className="text-[10px] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40">{p.n_nuovi} da classificare</Badge>
                    : <Badge variant="outline" className="text-[10px] text-[hsl(var(--success))] border-[hsl(var(--success))]/40">Verificato</Badge>}
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 hover:text-[hsl(var(--danger))]" data-testid={`periodo-delete-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); onDelete?.(p); }} title="Elimina periodo">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><div className="text-[11px] text-muted-foreground">Ricavi</div><div className="text-sm font-semibold tabular-nums">{formatCurrency(p.ricavi || 0)}</div></div>
                <div><div className="text-[11px] text-muted-foreground">EBITDA</div><div className={`text-sm font-semibold tabular-nums ${(p.ebitda || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(p.ebitda || 0)}</div></div>
                <div><div className="text-[11px] text-muted-foreground">Utile</div><div className={`text-sm font-semibold tabular-nums ${(p.utile_bilancio || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(p.utile_bilancio || 0)}</div></div>
              </div>
              <div className="mt-3 pt-3 border-t flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{p.n_accounts} conti · {p.n_movimenti} movimenti</span>
                <span className="inline-flex items-center gap-1 text-primary font-medium">Apri <ChevronRight className="h-3 w-3" /></span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  </div>
);

/* ---------- FORM RETTIFICA ---------- */
const ScostamentoForm = ({ sezioniRicavo, sezioniCosto, onSubmit, editing, onCancelEdit }) => {
  const [natura, setNatura] = useState('ricavo');
  const [voce, setVoce] = useState(VOCI_PRESET.ricavo[0]);
  const [label, setLabel] = useState('');
  const [sezione, setSezione] = useState('Ricavi');
  const [segno, setSegno] = useState('+');
  const [importo, setImporto] = useState('');
  const [busy, setBusy] = useState(false);

  const sezioniFor = (nat) => (nat === 'ricavo' ? (sezioniRicavo || []) : (sezioniCosto || []));

  useEffect(() => {
    if (editing) {
      setNatura(editing.natura || 'ricavo');
      setVoce(editing.voce || 'Altro');
      setLabel(editing.label || '');
      setSezione(editing.sezione || 'Ricavi');
      setSegno(editing.segno || ((editing.importo || 0) < 0 ? '-' : '+'));
      setImporto(String(Math.abs(editing.importo || 0)));
    }
  }, [editing]);

  const onNatura = (n) => {
    setNatura(n);
    setVoce(VOCI_PRESET[n][0]);
    const list = sezioniFor(n);
    setSezione(n === 'ricavo' ? 'Ricavi' : (list[0] || 'Costi Produttivi'));
  };

  const reset = () => { setLabel(''); setImporto(''); setVoce(VOCI_PRESET[natura][0]); };
  const submit = async () => {
    if (importo === '' || isNaN(Number(importo))) { toast.error('Inserisci un importo valido'); return; }
    if (!label.trim() && (!voce || voce === 'Altro')) { toast.error('Inserisci una descrizione'); return; }
    setBusy(true);
    try {
      await onSubmit({
        action: editing ? 'update' : 'add',
        id: editing?.id,
        natura, voce,
        label: label.trim() || voce,
        sezione, segno,
        importo: Math.abs(Number(importo)),
      });
      if (!editing) reset();
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end rounded-lg border p-3 bg-muted/20" data-testid="scostamento-form">
      <div className="md:col-span-2">
        <label className="text-[11px] text-muted-foreground">Natura</label>
        <Select value={natura} onValueChange={onNatura}>
          <SelectTrigger className="h-9" data-testid="scostamento-natura"><SelectValue /></SelectTrigger>
          <SelectContent>{NATURE.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <label className="text-[11px] text-muted-foreground">Voce</label>
        <Select value={voce} onValueChange={setVoce}>
          <SelectTrigger className="h-9" data-testid="scostamento-voce"><SelectValue /></SelectTrigger>
          <SelectContent>{VOCI_PRESET[natura].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-3">
        <label className="text-[11px] text-muted-foreground">Descrizione</label>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Es. SAL cantiere X" className="h-9" data-testid="scostamento-label" />
      </div>
      <div className="md:col-span-2">
        <label className="text-[11px] text-muted-foreground">Sezione impattata</label>
        <Select value={sezione} onValueChange={setSezione}>
          <SelectTrigger className="h-9" data-testid="scostamento-sezione"><SelectValue /></SelectTrigger>
          <SelectContent>{sezioniFor(natura).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-1">
        <label className="text-[11px] text-muted-foreground">Segno</label>
        <Select value={segno} onValueChange={setSegno}>
          <SelectTrigger className="h-9" data-testid="scostamento-segno"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="+">+</SelectItem><SelectItem value="-">−</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2 flex gap-2">
        <div className="flex-1">
          <label className="text-[11px] text-muted-foreground">Importo (€)</label>
          <Input type="number" value={importo} onChange={(e) => setImporto(e.target.value)} placeholder="0" className="h-9 tabular-nums" data-testid="scostamento-importo" />
        </div>
        <Button size="icon" className="h-9 w-9 shrink-0 self-end" onClick={submit} disabled={busy} data-testid="scostamento-add-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? <CheckCircle2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />)}
        </Button>
        {editing && <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0 self-end" onClick={onCancelEdit} data-testid="scostamento-cancel-btn">✕</Button>}
      </div>
    </div>
  );
};

/* ---------- DETTAGLIO PERIODO ---------- */
const PeriodoDetail = ({ pid, onBack, isAdmin, onReload }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recOpen, setRecOpen] = useState(false);
  const [movConto, setMovConto] = useState(null);
  const [movData, setMovData] = useState(null);
  const [movLoading, setMovLoading] = useState(false);
  const [partQuery, setPartQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  const load = () => { setLoading(true); return api.getFierPeriodo(pid).then(setData).catch(() => toast.error('Errore nel caricamento del periodo')).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [pid]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const openMov = async (conto) => {
    setMovConto(conto); setMovData(null); setMovLoading(true);
    try { setMovData(await api.getFierPeriodoMovimenti(pid, conto)); }
    catch { toast.error('Impossibile caricare i movimenti'); setMovConto(null); }
    finally { setMovLoading(false); }
  };

  // La classificazione scrive sul Piano dei Conti (unica fonte) e vale ovunque
  const assign = async (conto, sezione) => {
    try { await api.classificaFierPianoConti(undefined, { [conto]: sezione }); await load(); onReload?.(); toast.success('Classificazione aggiornata nel Piano dei Conti'); }
    catch { toast.error('Errore nella classificazione'); }
  };
  const confirmReconcile = async (assegnazioni) => {
    await api.classificaFierPianoConti(undefined, assegnazioni);
    setRecOpen(false);
    await load(); onReload?.();
    toast.success('Verifica completata · bilancio sbloccato');
  };
  const [editingScost, setEditingScost] = useState(null);
  const submitScostamento = async (payload) => {
    try {
      const d = await api.rettificaFierPeriodo(pid, payload);
      setData(d); setEditingScost(null);
      toast.success(payload.action === 'update' ? 'Scostamento aggiornato' : 'Scostamento salvato');
    } catch { toast.error('Errore nel salvataggio dello scostamento'); }
  };
  const delRettifica = async (id) => {
    try { const d = await api.rettificaFierPeriodo(pid, { action: 'delete', id }); setData(d); if (editingScost?.id === id) setEditingScost(null); toast.success('Scostamento eliminato'); }
    catch { toast.error('Errore eliminazione'); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    const next = [...messages, { role: 'user', content: msg }];
    setMessages(next); setInput(''); setSending(true);
    try { const res = await api.fierPeriodoChat(pid, { message: msg, history: next.slice(0, -1) }); setMessages([...next, { role: 'assistant', content: res.answer }]); }
    catch { setMessages([...next, { role: 'assistant', content: 'Si è verificato un errore nel generare la risposta. Riprova.' }]); toast.error('Errore assistente AI'); }
    finally { setSending(false); }
  };

  const s = data?.summary || {};
  const filteredConti = useMemo(() => {
    const q = partQuery.trim().toLowerCase();
    const list = data?.conti || [];
    if (!q) return list;
    return list.filter((a) => (a.conto || '').toLowerCase().includes(q) || (a.descrizione || '').toLowerCase().includes(q));
  }, [data, partQuery]);

  if (loading) return <Card className="p-10 text-center text-muted-foreground">Caricamento…</Card>;
  if (!data) return <Card className="p-10 text-center text-muted-foreground">Periodo non disponibile</Card>;

  return (
    <div className="space-y-6" data-testid="periodo-detail">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="periodo-back-btn"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><CalendarRange className="h-5 w-5 text-primary" /></div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Bilancio gestionale · {data.label}</h1>
            <p className="text-sm text-muted-foreground">{data.company} · {data.n_accounts} conti · {data.n_movimenti} movimenti</p>
          </div>
        </div>
        {data.pronto ? (
          <Badge variant="outline" className="text-xs text-[hsl(var(--success))] border-[hsl(var(--success))]/40 gap-1.5 py-1.5 px-3" data-testid="periodo-verificato-badge">
            <ShieldCheck className="h-3.5 w-3.5" />Verificato
          </Badge>
        ) : isAdmin ? (
          <Button variant="outline" className="border-[hsl(var(--warning))]/50 text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/10" onClick={() => setRecOpen(true)} data-testid="periodo-verifica-btn">
            <AlertCircle className="h-4 w-4 mr-2" />Verifica {data.n_nuovi} conti
          </Button>
        ) : (
          <Badge variant="outline" className="text-xs text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40 gap-1.5 py-1.5 px-3" data-testid="periodo-nuovi-badge">
            <AlertCircle className="h-3.5 w-3.5" />{data.n_nuovi} conti da verificare
          </Badge>
        )}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Ricavi', value: s.ricavi, tone: 'text-foreground' },
          { label: 'I Margine Operativo', value: s.i_margine, tone: (s.i_margine || 0) >= 0 ? 'text-foreground' : 'text-[hsl(var(--danger))]' },
          { label: 'EBITDA / MOL', value: s.ebitda, tone: (s.ebitda || 0) >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]' },
          { label: 'Utile di bilancio', value: s.utile_bilancio, tone: (s.utile_bilancio || 0) >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]' },
        ].map((k) => (
          <Card key={k.label} className="p-4" data-testid={`periodo-kpi-${k.label}`}>
            <div className="text-xs text-muted-foreground">{k.label}</div>
            {data.pronto
              ? <div className={`text-xl sm:text-2xl font-semibold tabular-nums mt-1 truncate ${k.tone}`}>{formatCurrency(k.value || 0)}</div>
              : <div className="text-xl sm:text-2xl font-semibold tabular-nums mt-1 text-muted-foreground/40 flex items-center gap-1.5"><Lock className="h-4 w-4" />—</div>}
          </Card>
        ))}
      </div>

      <Tabs defaultValue="ce" className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="ce" data-testid="periodo-tab-ce">Bilancio Gestionale</TabsTrigger>
          <TabsTrigger value="ric" data-testid="periodo-tab-ric">
            <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />Riconciliazione
            {data.n_nuovi > 0 && <span className="ml-1.5 text-[hsl(var(--warning))]">({data.n_nuovi})</span>}
          </TabsTrigger>
          <TabsTrigger value="part" data-testid="periodo-tab-part"><ListTree className="h-3.5 w-3.5 mr-1.5" />Partitario</TabsTrigger>
          <TabsTrigger value="ai" data-testid="periodo-tab-ai"><Sparkles className="h-3.5 w-3.5 mr-1.5" />Assistente AI</TabsTrigger>
        </TabsList>

        {/* Bilancio Gestionale */}
        <TabsContent value="ce" className="mt-4">
          {!data.pronto ? (
            <Card className="p-10 text-center" data-testid="periodo-ce-locked">
              <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--warning))]/10 flex items-center justify-center mx-auto mb-4"><Lock className="h-6 w-6 text-[hsl(var(--warning))]" /></div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Bilancio bloccato · verifica conti richiesta</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-5">
                Ci sono <b className="text-foreground">{data.n_nuovi}</b> conti non ancora allineati al Piano dei Conti. Completa la verifica per generare il bilancio riclassificato di periodo.
              </p>
              {isAdmin
                ? <Button onClick={() => setRecOpen(true)} data-testid="periodo-ce-verifica-btn"><ShieldCheck className="h-4 w-4 mr-2" />Completa la verifica ora</Button>
                : <p className="text-xs text-muted-foreground">La verifica può essere completata da un amministratore.</p>}
            </Card>
          ) : (
          <Card className="p-0 overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Conto Economico gestionale riclassificato · {data.label}</h3>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8" data-testid="periodo-ce-export-btn"
                  onClick={() => exportCEPdf({ company: data.company || 'Fier S.p.A.', title: 'Bilancio gestionale riclassificato di periodo', period: data.label, rows: data.ce_rows || [], pctFraction: true })}>
                  <FileDown className="h-3.5 w-3.5 mr-1.5" />Esporta PDF
                </Button>
                <Badge variant="outline" className="text-[10px] hidden sm:inline-flex">schema {data.company}</Badge>
              </div>
            </div>
            <div className="max-h-[64vh] overflow-y-auto overflow-x-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow><TableHead className="w-[56%]">Voce</TableHead><TableHead className="text-right hidden sm:table-cell">% ricavi</TableHead><TableHead className="text-right">Valore</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {(data.ce_rows || []).map((r, i) => {
                    if (r.kind === 'section') {
                      return <TableRow key={i} className="bg-muted/40"><TableCell colSpan={3} className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground py-2">{r.label}</TableCell></TableRow>;
                    }
                    if (r.kind === 'rettifica') {
                      return (
                        <TableRow key={i} className="bg-primary/5" data-testid={`periodo-ce-rettifica-${i}`}>
                          <TableCell className="pl-6 text-primary text-sm whitespace-normal break-words">{r.label}{r.nota ? <span className="block text-[11px] text-muted-foreground">{r.nota}</span> : null}</TableCell>
                          <TableCell className="hidden sm:table-cell"></TableCell>
                          <TableCell className="text-right tabular-nums text-primary">{formatCurrency(r.value)}</TableCell>
                        </TableRow>
                      );
                    }
                    const isTotal = r.kind === 'total';
                    return (
                      <TableRow key={i} className={isTotal ? (r.strong ? 'bg-primary/5' : 'bg-muted/20') : ''} data-testid={`periodo-ce-row-${i}`}>
                        <TableCell className={`whitespace-normal break-words ${isTotal ? 'font-semibold' : ''} ${r.kind === 'account' ? 'pl-6' : ''}`}>
                          {r.code && <span className="text-[11px] text-muted-foreground mr-2 tabular-nums">{r.code}</span>}{r.label}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground hidden sm:table-cell">{r.pct != null ? formatPercent(r.pct, 1, true) : ''}</TableCell>
                        <TableCell className={`text-right tabular-nums ${isTotal ? 'font-semibold' : ''} ${(r.value || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{r.value != null ? formatCurrency(r.value) : ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
          )}
        </TabsContent>

        {/* Riconciliazione */}
        <TabsContent value="ric" className="mt-4 space-y-4">
          <Card className="p-0 overflow-hidden">
            <div className="px-5 py-3 border-b">
              <h3 className="text-sm font-semibold">Conti nuovi da classificare {data.n_nuovi > 0 ? `(${data.n_nuovi})` : ''}</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Assegna una sezione ai conti. Le scelte aggiornano il <b className="text-foreground">Piano dei Conti</b> (unica fonte) e valgono in tutta l'app.</p>
            </div>
            <div className="max-h-[40vh] overflow-y-auto overflow-x-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow><TableHead className="w-[100px] md:w-[120px]">Conto</TableHead><TableHead>Descrizione</TableHead><TableHead className="text-right hidden md:table-cell">Saldo</TableHead><TableHead className="w-[150px] md:w-[220px]">Assegna a sezione</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {(data.nuovi || []).map((a, i) => (
                    <TableRow key={a.conto} data-testid={`periodo-nuovo-row-${i}`}>
                      <TableCell className="tabular-nums text-xs text-muted-foreground align-top">{a.conto}</TableCell>
                      <TableCell className="text-sm max-w-[130px] whitespace-normal break-words md:max-w-none">{a.descrizione}</TableCell>
                      <TableCell className={`text-right tabular-nums hidden md:table-cell ${(a.saldo || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(a.saldo)}</TableCell>
                      <TableCell>
                        <Select onValueChange={(v) => assign(a.conto, v)} disabled={!isAdmin}>
                          <SelectTrigger className="h-8" data-testid={`periodo-assign-${a.conto}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                          <SelectContent>{(data.sezioni_disponibili || []).map((sz) => <SelectItem key={sz} value={sz}>{sz}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(data.nuovi || []).length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center text-[hsl(var(--success))] py-6">Tutti i conti sono riconciliati ✓</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Card className="p-5 space-y-4" id="scostamenti-section" data-testid="scostamenti-card">
            <div>
              <h3 className="text-sm font-semibold">Scostamenti</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Ricavi (es. fatture da emettere, SAL, margine su prodotti non usciti) o costi (es. fatture da ricevere, costi da sostenere, costi non di competenza). Ogni scostamento è una somma (+) o sottrazione (−) algebrica sulla sezione indicata e rientra nel bilancio di periodo.</p>
            </div>
            {isAdmin && <ScostamentoForm sezioniRicavo={data.sezioni_ricavo || []} sezioniCosto={data.sezioni_costo || []} onSubmit={submitScostamento} editing={editingScost} onCancelEdit={() => setEditingScost(null)} />}
            <div className="border rounded-lg overflow-hidden overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Natura</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead className="hidden md:table-cell">Sezione</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead className="w-[140px] hidden sm:table-cell">Ultima modifica</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.rettifiche || []).map((r) => {
                    const isRic = (r.natura || (r.importo >= 0 ? 'ricavo' : 'costo')) === 'ricavo';
                    return (
                      <TableRow key={r.id} data-testid={`periodo-scostamento-${r.id}`}>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${isRic ? 'text-[hsl(var(--success))] border-[hsl(var(--success))]/40' : 'text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40'}`}>{isRic ? 'Ricavo' : 'Costo'}</Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-[160px] whitespace-normal break-words md:max-w-none">
                          {r.label}
                          {r.voce && r.voce !== r.label ? <span className="block text-[11px] text-muted-foreground">{r.voce}</span> : null}
                          {r.nota ? <span className="block text-[11px] text-muted-foreground">{r.nota}</span> : null}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground hidden md:table-cell">{SPECIAL_LABEL[r.sezione] || r.sezione}</TableCell>
                        <TableCell className={`text-right tabular-nums font-medium ${(r.importo || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{(r.importo || 0) >= 0 ? '+' : '−'} {formatCurrency(Math.abs(r.importo || 0))}</TableCell>
                        <TableCell className="text-[11px] text-muted-foreground tabular-nums hidden sm:table-cell whitespace-nowrap">{fmtDateShort(r.updated_at)}</TableCell>
                        <TableCell className="text-right">
                          {isAdmin && (
                            <div className="flex items-center justify-end gap-0.5">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingScost(r); document.getElementById('scostamenti-section')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid={`periodo-scostamento-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delRettifica(r.id)} data-testid={`periodo-scostamento-del-${r.id}`}><Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" /></Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(data.rettifiche || []).length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nessuno scostamento inserito</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* Partitario */}
        <TabsContent value="part" className="mt-4">
          <Card className="p-0 overflow-hidden">
            <div className="px-5 py-3 border-b flex flex-col sm:flex-row sm:items-center gap-3">
              <h3 className="text-sm font-semibold sm:mr-auto">Partitario di periodo · {data.n_accounts} conti</h3>
              <div className="relative w-full sm:w-64">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input value={partQuery} onChange={(e) => setPartQuery(e.target.value)} placeholder="Cerca conto o descrizione…" className="pl-8 h-9" data-testid="periodo-part-search" />
              </div>
            </div>
            <div className="max-h-[62vh] overflow-y-auto overflow-x-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow><TableHead className="w-[100px] md:w-[120px]">Conto</TableHead><TableHead>Descrizione</TableHead><TableHead className="hidden md:table-cell">Sezione</TableHead><TableHead className="text-right hidden md:table-cell">Movimenti</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead className="w-[40px]"></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {filteredConti.map((a, i) => (
                    <TableRow key={a.conto} className="cursor-pointer hover:bg-accent/60 transition-colors" onClick={() => openMov(a.conto)} data-testid={`periodo-part-row-${i}`}>
                      <TableCell className="tabular-nums text-xs text-muted-foreground align-top">{a.conto}</TableCell>
                      <TableCell className="text-sm max-w-[150px] whitespace-normal break-words md:max-w-none">{a.descrizione}</TableCell>
                      <TableCell className="hidden md:table-cell">{a.sezione ? <Badge variant="outline" className="text-[10px]">{a.sezione}</Badge> : <span className="text-[11px] text-[hsl(var(--warning))]">non classificato</span>}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground hidden md:table-cell">{a.n_movimenti}</TableCell>
                      <TableCell className={`text-right tabular-nums font-medium ${(a.saldo || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(a.saldo)}</TableCell>
                      <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))}
                  {filteredConti.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nessun conto trovato</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* Assistente AI */}
        <TabsContent value="ai" className="mt-4">
          <Card className="flex flex-col h-[62vh]" data-testid="periodo-ai-panel">
            <div className="px-5 py-3 border-b flex items-center gap-2"><Bot className="h-4 w-4 text-primary" /><h3 className="text-sm font-semibold">Assistente AI · periodo {data.label}</h3></div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center text-muted-foreground py-6">
                  <Sparkles className="h-8 w-8 mx-auto mb-3 text-primary/60" />
                  <p className="text-sm mb-4">Fai una domanda sul bilancio gestionale del periodo {data.label}.</p>
                  <div className="flex flex-wrap gap-2 justify-center max-w-xl mx-auto">
                    {SUGGESTIONS.map((q) => <button key={q} onClick={() => send(q)} className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/50 hover:bg-accent transition-colors" data-testid="periodo-ai-suggestion">{q}</button>)}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`periodo-ai-msg-${m.role}`}>
                  {m.role === 'assistant' && <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-primary" /></div>}
                  <div className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground whitespace-pre-wrap' : 'bg-muted text-foreground'}`}>
                    {m.role === 'assistant' ? <MarkdownMessage content={m.content} /> : m.content}
                  </div>
                  {m.role === 'user' && <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0"><User className="h-4 w-4" /></div>}
                </div>
              ))}
              {sending && <div className="flex gap-3 justify-start"><div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-primary" /></div><div className="bg-muted rounded-xl px-4 py-2.5 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Sto analizzando…</div></div>}
              <div ref={chatEndRef} />
            </div>
            <div className="border-t p-3 flex items-center gap-2">
              <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={`Scrivi una domanda sul periodo ${data.label}…`} disabled={sending} data-testid="periodo-ai-input" />
              <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="periodo-ai-send"><Send className="h-4 w-4" /></Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Drill-down movimenti */}
      <Dialog open={!!movConto} onOpenChange={(o) => { if (!o) { setMovConto(null); setMovData(null); } }}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="periodo-mov-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><ListTree className="h-4 w-4 text-primary" />{movData ? `${movData.conto} · ${movData.descrizione}` : `Conto ${movConto || ''}`}</DialogTitle></DialogHeader>
          {movLoading ? (
            <div className="py-10 text-center text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Caricamento movimenti…</div>
          ) : movData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[{ label: 'Saldo apertura', value: movData.saldo_apertura }, { label: 'Totale dare', value: movData.tot_dare }, { label: 'Totale avere', value: movData.tot_avere }, { label: 'Saldo', value: movData.saldo, strong: true }].map((k) => (
                  <div key={k.label} className="rounded-lg border p-2.5"><div className="text-[11px] text-muted-foreground">{k.label}</div><div className={`text-sm tabular-nums mt-0.5 ${k.strong ? 'font-semibold' : ''} ${(k.value || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(k.value || 0)}</div></div>
                ))}
              </div>
              <div className="max-h-[46vh] overflow-y-auto overflow-x-auto border rounded-lg mt-1">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10"><TableRow><TableHead className="w-[90px]">Data</TableHead><TableHead>Descrizione</TableHead><TableHead className="text-right">Importo</TableHead><TableHead className="text-right hidden sm:table-cell">Saldo progr.</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(movData.movements || []).map((m, i) => (
                      <TableRow key={i} data-testid={`periodo-mov-row-${i}`}>
                        <TableCell className="tabular-nums text-xs text-muted-foreground align-top">{m.data}</TableCell>
                        <TableCell className="text-sm whitespace-normal break-words">{m.descr}{m.controparte && <span className="block text-[11px] text-muted-foreground">{m.controparte}</span>}</TableCell>
                        <TableCell className={`text-right tabular-nums ${(m.importo || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(m.importo)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground hidden sm:table-cell">{formatCurrency(m.saldo)}</TableCell>
                      </TableRow>
                    ))}
                    {(movData.movements || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nessun movimento</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Popup verifica conti (dal dettaglio periodo) */}
      <ReconciliationDialog
        open={recOpen}
        onOpenChange={setRecOpen}
        nuovi={data.nuovi}
        sezioni={data.sezioni_disponibili}
        voceEsclusione="Escluso"
        onConfirm={confirmReconcile}
      />
    </div>
  );
};

/* ---------- WIZARD "CARICA BILANCIO" ---------- */
const WIZARD_TYPES = [
  { v: 'periodo', title: 'Bilancio di periodo', desc: 'Mastrino gestionale in PDF (es. al 30.06)', accept: '.pdf', icon: CalendarRange, badge: 'PDF' },
  { v: 'bilancio', title: 'Bilancio annuale ufficiale', desc: 'Bilancio riclassificato annuale in Excel', accept: '.xlsx,.xlsm', icon: Landmark, badge: 'Excel' },
  { v: 'partitario', title: 'Partitario annuale', desc: 'Mastrino annuale completo in PDF (quadratura)', accept: '.pdf', icon: ListTree, badge: 'PDF' },
];
const WIZARD_TYPE_MAP = WIZARD_TYPES.reduce((a, o) => { a[o.v] = o; return a; }, {});

const StepDot = ({ n, step, label }) => {
  const done = step > n;
  const active = step === n;
  return (
    <div className="flex items-center gap-2">
      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 transition-colors
        ${done ? 'bg-[hsl(var(--success))] text-white' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
        {done ? <Check className="h-3.5 w-3.5" /> : n}
      </div>
      <span className={`text-xs hidden sm:inline ${active ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{label}</span>
    </div>
  );
};

const PreviewStat = ({ label, value }) => (
  <div className="rounded-lg border p-2.5 bg-muted/20">
    <div className="text-[11px] text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold tabular-nums mt-0.5 truncate">{value}</div>
  </div>
);

const UploadWizard = ({ open, onOpenChange, onComplete }) => {
  const [step, setStep] = useState(1);
  const [tipo, setTipo] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [anno, setAnno] = useState('');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setStep(1); setTipo(null); setFile(null); setPreview(null);
    setBusy(false); setSaving(false); setLabel(''); setPeriodEnd(''); setAnno(''); setConfirmReplace(false);
  };
  useEffect(() => { if (open) reset(); }, [open]);

  const cfg = tipo ? WIZARD_TYPE_MAP[tipo] : null;
  const isAnnual = tipo === 'bilancio' || tipo === 'partitario';

  const pickType = (v) => { setTipo(v); setStep(2); setTimeout(() => inputRef.current?.click(), 100); };

  const runPreview = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const p = await api.uploadFierPreview(fd, tipo);
      setPreview(p);
      if (tipo === 'periodo') { setLabel(p.label || ''); setPeriodEnd(p.period_end || ''); }
      else { setAnno(p.anno ? String(p.anno) : ''); }
      setConfirmReplace(false);
      setStep(3);
    } catch (err) {
      toast.error(api.apiError(err, 'Impossibile leggere il file'));
      setFile(null);
    } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); runPreview(f); }
    if (inputRef.current) inputRef.current.value = '';
  };

  const canSubmit = (() => {
    if (!preview || saving) return false;
    if (preview.exists && !confirmReplace) return false;
    if (tipo === 'periodo') return !!periodEnd;
    return !!anno;
  })();

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      if (tipo === 'periodo') {
        const res = await api.uploadFierPeriodo(fd, { label: label.trim() || undefined, period_end: periodEnd || undefined });
        onComplete('periodo', res);
      } else if (tipo === 'bilancio') {
        const res = await api.uploadFierBilancio(fd, anno || undefined);
        onComplete('bilancio', res);
      } else {
        const res = await api.uploadFierPartitario(fd, anno || undefined);
        onComplete('partitario', res);
      }
    } catch (err) {
      toast.error(api.apiError(err, 'Errore durante il caricamento'));
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="upload-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileUp className="h-4 w-4 text-primary" />Carica bilancio
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-2 px-1">
          <StepDot n={1} step={step} label="Tipo" />
          <div className="h-px flex-1 bg-border" />
          <StepDot n={2} step={step} label="File" />
          <div className="h-px flex-1 bg-border" />
          <StepDot n={3} step={step} label="Anteprima" />
        </div>

        {/* STEP 1 · Tipo */}
        {step === 1 && (
          <div className="space-y-2 pt-1" data-testid="wizard-step-type">
            <p className="text-sm text-muted-foreground">Scegli il tipo di documento da caricare.</p>
            {WIZARD_TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.v} onClick={() => pickType(t.v)} data-testid={`wizard-type-${t.v}`}
                  className="w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:border-primary/50 hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-primary" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{t.title}</span>
                      <Badge variant="outline" className="text-[10px]">{t.badge}</Badge>
                    </div>
                    <p className="text-[12px] text-muted-foreground truncate">{t.desc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              );
            })}
          </div>
        )}

        {/* STEP 2 · File */}
        {step === 2 && cfg && (
          <div className="space-y-4 pt-1" data-testid="wizard-step-file">
            <input ref={inputRef} type="file" accept={cfg.accept} className="hidden" onChange={onFile} data-testid="wizard-file-input" />
            <button onClick={() => inputRef.current?.click()} disabled={busy}
              className="w-full rounded-xl border-2 border-dashed p-8 flex flex-col items-center gap-3 text-center transition-colors hover:border-primary/50 hover:bg-accent/40 disabled:opacity-60">
              {busy ? <Loader2 className="h-8 w-8 text-primary animate-spin" /> : <FileUp className="h-8 w-8 text-primary" />}
              <div>
                <div className="text-sm font-medium">{busy ? 'Analisi del file in corso…' : `Seleziona ${cfg.title.toLowerCase()}`}</div>
                <div className="text-[12px] text-muted-foreground mt-0.5">Formati accettati: {cfg.accept}</div>
              </div>
            </button>
            <div className="flex justify-start">
              <Button variant="ghost" size="sm" onClick={() => { setStep(1); setTipo(null); }} disabled={busy} data-testid="wizard-back-1">
                <ArrowLeft className="h-4 w-4 mr-1.5" />Cambia tipo
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 · Anteprima */}
        {step === 3 && preview && cfg && (
          <div className="space-y-4 pt-1" data-testid="wizard-step-preview">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="truncate text-muted-foreground" title={preview.filename}>{preview.filename}</span>
            </div>

            {/* Metadati rilevati */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <PreviewStat label="Conti" value={preview.n_accounts ?? '—'} />
              {tipo === 'bilancio'
                ? <PreviewStat label="Righe CE" value={preview.n_ce_rows ?? '—'} />
                : <PreviewStat label="Movimenti" value={preview.n_movimenti ?? '—'} />}
              <PreviewStat label="Ricavi" value={preview.ricavi != null ? formatCurrency(preview.ricavi) : '—'} />
              {tipo !== 'partitario' && <>
                <PreviewStat label="EBITDA" value={preview.ebitda != null ? formatCurrency(preview.ebitda) : '—'} />
                <PreviewStat label="Utile" value={preview.utile != null ? formatCurrency(preview.utile) : '—'} />
              </>}
              {tipo === 'periodo' && (
                <div className="rounded-lg border p-2.5 bg-muted/20">
                  <div className="text-[11px] text-muted-foreground">Conti da classificare</div>
                  <div className={`text-sm font-semibold tabular-nums mt-0.5 ${preview.n_nuovi > 0 ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--success))]'}`}>{preview.n_nuovi ?? 0}</div>
                </div>
              )}
            </div>

            {/* Campi editabili */}
            <div className="space-y-3 rounded-lg border p-3 bg-muted/10">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Verifica e correggi i dati</div>
              {tipo === 'periodo' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-muted-foreground">Etichetta / descrizione</label>
                    <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Es. 30.06.2026" className="h-9 mt-0.5" data-testid="wizard-label" />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground">Data fine periodo</label>
                    <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="h-9 mt-0.5 tabular-nums" data-testid="wizard-period-end" />
                    {!periodEnd && <p className="text-[11px] text-[hsl(var(--warning))] mt-1">Data non rilevata: inseriscila per continuare.</p>}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-muted-foreground">Anno di riferimento</label>
                    <Input type="number" value={anno} onChange={(e) => setAnno(e.target.value)} placeholder="Es. 2025" className="h-9 mt-0.5 tabular-nums" data-testid="wizard-anno" />
                    {!anno && <p className="text-[11px] text-[hsl(var(--warning))] mt-1">Anno non rilevato: inseriscilo per continuare.</p>}
                  </div>
                </div>
              )}
            </div>

            {/* Warning sostituzione */}
            {preview.exists && (
              <div className="rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 space-y-2" data-testid="wizard-replace-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))] mt-0.5 shrink-0" />
                  <p className="text-[13px] text-foreground">
                    Esiste già un {tipo === 'periodo' ? 'bilancio di periodo per questa data' : `bilancio per l'anno ${anno || 'indicato'}`}.
                    Procedendo verrà <b>sostituito</b>{tipo === 'periodo' ? ' (scostamenti e classificazioni esistenti vengono mantenuti)' : ''}.
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer pl-6">
                  <Checkbox checked={confirmReplace} onCheckedChange={(v) => setConfirmReplace(!!v)} data-testid="wizard-confirm-replace" />
                  <span className="text-[13px] font-medium">Confermo la sostituzione</span>
                </label>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => { setStep(2); setPreview(null); setFile(null); }} disabled={saving} data-testid="wizard-back-2">
                <ArrowLeft className="h-4 w-4 mr-1.5" />Cambia file
              </Button>
              <Button onClick={submit} disabled={!canSubmit} data-testid="wizard-submit">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                {saving ? 'Caricamento…' : (preview.exists ? 'Sostituisci e carica' : 'Carica')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------- POPUP SCOSTAMENTI (al caricamento) ---------- */
const ScostamentiDialog = ({ pid, open, onOpenChange, onDone }) => {
  const [view, setView] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    if (open && pid) { setView(null); setEditing(null); api.getFierPeriodo(pid).then(setView).catch(() => {}); }
  }, [open, pid]);
  const submit = async (payload) => {
    try { const d = await api.rettificaFierPeriodo(pid, payload); setView(d); setEditing(null); toast.success(payload.action === 'update' ? 'Scostamento aggiornato' : 'Scostamento aggiunto'); }
    catch { toast.error('Errore nel salvataggio dello scostamento'); }
  };
  const del = async (id) => {
    try { const d = await api.rettificaFierPeriodo(pid, { action: 'delete', id }); setView(d); if (editing?.id === id) setEditing(null); }
    catch { toast.error('Errore eliminazione'); }
  };
  const list = view?.rettifiche || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="scostamenti-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><SlidersHorizontal className="h-4 w-4 text-primary" />Inserisci scostamenti{view?.label ? ` · ${view.label}` : ''}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Aggiungi eventuali scostamenti di <b>ricavo</b> (fatture da emettere, SAL, margine su prodotti non usciti…) o di <b>costo</b> (fatture da ricevere, costi da sostenere, non di competenza…). Puoi anche <b>saltare</b> e inserirli in seguito dalla sezione Scostamenti.</p>
        <ScostamentoForm sezioniRicavo={view?.sezioni_ricavo || []} sezioniCosto={view?.sezioni_costo || []} onSubmit={submit} editing={editing} onCancelEdit={() => setEditing(null)} />
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow><TableHead className="w-[80px]">Natura</TableHead><TableHead>Descrizione</TableHead><TableHead className="hidden sm:table-cell">Sezione</TableHead><TableHead className="text-right">Importo</TableHead><TableHead className="w-[80px]"></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {list.map((r) => {
                const isRic = (r.natura || (r.importo >= 0 ? 'ricavo' : 'costo')) === 'ricavo';
                return (
                  <TableRow key={r.id} data-testid={`scostamenti-dialog-row-${r.id}`}>
                    <TableCell><Badge variant="outline" className={`text-[10px] ${isRic ? 'text-[hsl(var(--success))] border-[hsl(var(--success))]/40' : 'text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40'}`}>{isRic ? 'Ricavo' : 'Costo'}</Badge></TableCell>
                    <TableCell className="text-sm">{r.label}{r.voce && r.voce !== r.label ? <span className="block text-[11px] text-muted-foreground">{r.voce}</span> : null}</TableCell>
                    <TableCell className="text-xs text-muted-foreground hidden sm:table-cell">{SPECIAL_LABEL[r.sezione] || r.sezione}</TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${(r.importo || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{(r.importo || 0) >= 0 ? '+' : '−'} {formatCurrency(Math.abs(r.importo || 0))}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(r)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => del(r.id)}><Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {list.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Nessuno scostamento inserito</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onDone(pid)} data-testid="scostamenti-skip">Salta</Button>
          <Button onClick={() => onDone(pid)} data-testid="scostamenti-done"><CheckCircle2 className="h-4 w-4 mr-2" />Fine · vai al bilancio</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- PAGINA ---------- */
export default function BilanciPeriodo() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'control'; // sezione Analisi CG aperta al Controllo di gestione
  const [periodi, setPeriodi] = useState([]);
  const [annuali, setAnnuali] = useState([]);
  const [selected, setSelected] = useState(null);
  const [annualYear, setAnnualYear] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [reconcile, setReconcile] = useState({ open: false, nuovi: [], sezioni: [], voce: 'Escluso', pid: null });
  const [toDelete, setToDelete] = useState(null);
  const [scostPid, setScostPid] = useState(null);

  const loadList = () => api.getFierPeriodi().then(setPeriodi).catch(() => toast.error('Errore nel caricamento dei periodi'));
  const loadAnnuali = () => api.getFierBilanci().then((r) => setAnnuali(Array.isArray(r) ? r : (r?.anni || r?.years || []))).catch(() => {});
  useEffect(() => { loadList(); loadAnnuali(); }, []);

  const handleWizardComplete = async (tipo, res) => {
    setWizardOpen(false);
    if (tipo === 'periodo') {
      await loadList();
      if (res.n_nuovi > 0) {
        toast.info(`${res.label}: ${res.n_nuovi} conti da allineare prima di generare il bilancio`);
        setReconcile({ open: true, nuovi: res.nuovi || [], sezioni: res.sezioni_disponibili || [], voce: res.voce_esclusione || 'Escluso', pid: res.id });
      } else {
        toast.success(`Periodo ${res.label} importato e verificato · ${res.n_accounts} conti`);
        setScostPid(res.id);
      }
    } else if (tipo === 'bilancio') {
      await loadAnnuali();
      toast.success(`Bilancio annuale ${res.year} importato · ${res.n_accounts} conti`);
    } else if (tipo === 'partitario') {
      await loadAnnuali();
      toast.success(`Partitario ${res.year} importato · ${res.n_accounts} conti, ${res.n_movimenti} movimenti`);
    }
  };

  const confirmReconcile = async (assegnazioni) => {
    await api.classificaFierPianoConti(undefined, assegnazioni);
    toast.success('Conti allineati · Piano dei Conti aggiornato');
    const pid = reconcile.pid;
    setReconcile({ open: false, nuovi: [], sezioni: [], voce: 'Escluso', pid: null });
    await loadList();
    if (pid) setScostPid(pid);
  };

  const doneScostamenti = (pid) => { setScostPid(null); if (pid) setSelected(pid); };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.deleteFierPeriodo(toDelete.id);
      toast.success(`Periodo ${toDelete.label} eliminato`);
      setToDelete(null);
      await loadList();
    } catch {
      toast.error('Errore durante l\u2019eliminazione');
    }
  };

  return (
    <>
      {annualYear != null
        ? <FierSpa embedded initialYear={annualYear} onBack={() => { setAnnualYear(null); loadAnnuali(); }} />
        : selected
          ? <PeriodoDetail pid={selected} isAdmin={isAdmin} onBack={() => { setSelected(null); loadList(); }} onReload={loadList} />
          : <PeriodiList annuali={annuali} periodi={periodi} onOpen={setSelected} onOpenAnnual={setAnnualYear} onOpenWizard={() => setWizardOpen(true)} isAdmin={isAdmin} onDelete={setToDelete} />}
      <UploadWizard open={wizardOpen} onOpenChange={setWizardOpen} onComplete={handleWizardComplete} />
      <ReconciliationDialog
        open={reconcile.open}
        onOpenChange={(o) => setReconcile((r) => ({ ...r, open: o }))}
        nuovi={reconcile.nuovi}
        sezioni={reconcile.sezioni}
        voceEsclusione={reconcile.voce}
        onConfirm={confirmReconcile}
      />
      <ScostamentiDialog
        pid={scostPid}
        open={!!scostPid}
        onOpenChange={(o) => { if (!o) { const p = scostPid; setScostPid(null); if (p) setSelected(p); } }}
        onDone={doneScostamenti}
      />
      <Dialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <DialogContent className="max-w-md" data-testid="periodo-delete-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />Elimina periodo</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Vuoi eliminare il periodo <b className="text-foreground">{toDelete?.label}</b>? L'operazione non è reversibile. Il Piano dei Conti non viene modificato.</p>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setToDelete(null)} data-testid="periodo-delete-cancel">Annulla</Button>
            <Button className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" onClick={confirmDelete} data-testid="periodo-delete-confirm"><Trash2 className="h-4 w-4 mr-2" />Elimina</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
